import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRolePermissions, useUserOverrides } from '@/hooks/usePermissions';
import { useUpdatePermissionOverrides, type TeamMember } from '@/hooks/useTeamManagement';
import {
  PERMISSION_DOMAINS, DOMAIN_ACTIONS,
} from '@/lib/permissions';
import { useTranslation } from 'react-i18next';

interface Props {
  member: TeamMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type OverrideState = Record<string, { granted: boolean; scope: string }>;

export function PermissionEditor({ member, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const isLocked = member.role_definition_slug === 'org_owner' || member.role_definition_slug === 'org_admin' || member.role === 'org_owner' || member.role === 'org_admin';
  const { data: rolePerms } = useRolePermissions(member.role_definition_id);
  const { data: existingOverrides } = useUserOverrides(isLocked ? null : member.user_id);
  const updateOverrides = useUpdatePermissionOverrides();

  const [overrides, setOverrides] = useState<OverrideState>({});

  const rolePermSet = useMemo(() => {
    const set = new Set<string>();
    rolePerms?.forEach(p => set.add(`${p.domain}:${p.action}`));
    return set;
  }, [rolePerms]);

  useEffect(() => {
    const state: OverrideState = {};
    existingOverrides?.forEach(o => {
      state[`${o.domain}:${o.action}`] = { granted: o.granted, scope: o.scope };
    });
    setOverrides(state);
  }, [existingOverrides]);

  const getEffectiveState = (domain: string, action: string): 'role' | 'granted' | 'denied' | 'none' => {
    const key = `${domain}:${action}`;
    const override = overrides[key];

    if (override !== undefined) {
      if (rolePermSet.has(key) && !override.granted) return 'denied';
      if (!rolePermSet.has(key) && override.granted) return 'granted';
    }

    if (rolePermSet.has(key)) return 'role';
    return 'none';
  };

  const togglePermission = (domain: string, action: string) => {
    const key = `${domain}:${action}`;
    const hasRolePerm = rolePermSet.has(key);
    const currentOverride = overrides[key];

    setOverrides(prev => {
      const next = { ...prev };

      if (hasRolePerm) {
        if (currentOverride?.granted === false) {
          delete next[key];
        } else {
          next[key] = { granted: false, scope: 'all' };
        }
      } else {
        if (currentOverride?.granted === true) {
          delete next[key];
        } else {
          next[key] = { granted: true, scope: 'all' };
        }
      }

      return next;
    });
  };

  const handleSave = async () => {
    const overrideList = Object.entries(overrides).map(([key, val]) => {
      const [domain, action] = key.split(':');
      return { domain, action, scope: val.scope, granted: val.granted };
    });

    await updateOverrides.mutateAsync({
      target_user_id: member.user_id,
      overrides: overrideList,
    });
    onOpenChange(false);
  };

  const overrideCount = Object.keys(overrides).length;
  const domainLabel = (domain: string) => t(`hire.permissions.domains.${domain}`);
  const actionLabel = (action: string) => t(`hire.permissions.actions.${action}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isLocked ? t('hire.team.usersList.actions.viewPermissions') : t('hire.team.usersList.actions.customizePermissions')} - {member.full_name}
          </DialogTitle>
          <DialogDescription>
            {isLocked ? (
              <span>
                {t('hire.permissions.lockedDescription', { role: member.role_definition_name })}
              </span>
            ) : (
              <>
                {t('hire.permissions.overrideDescription', { role: member.role_definition_name })}
                <span className="block mt-1 text-xs">
                  <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 me-1">{t('hire.permissions.legend.role')}</Badge>
                  {t('hire.permissions.legend.defaultFromRole')}
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20 mx-1">{t('hire.permissions.legend.added')}</Badge>
                  {t('hire.permissions.legend.overrideGranted')}
                  <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 mx-1">{t('hire.permissions.legend.removed')}</Badge>
                  {t('hire.permissions.legend.overrideDenied')}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pe-3">
          <div className="space-y-6 py-2">
            {PERMISSION_DOMAINS.map(domain => {
              const actions = DOMAIN_ACTIONS[domain] || [];
              return (
                <div key={domain} className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground border-b border-border pb-1">
                    {domainLabel(domain)}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {actions.map(action => {
                      const state = getEffectiveState(domain, action);
                      const isChecked = state === 'role' || state === 'granted';
                      const isOverride = state === 'granted' || state === 'denied';

                      return (
                        <label
                          key={action}
                          className={`
                            flex items-center gap-2 p-2 rounded-lg text-sm
                            border transition-colors
                            ${isLocked ? 'cursor-default' : 'cursor-pointer'}
                            ${state === 'granted' ? 'border-success/30 bg-success/5' : ''}
                            ${state === 'denied' ? 'border-destructive/30 bg-destructive/5' : ''}
                            ${state === 'role' ? 'border-border bg-accent/5' : ''}
                            ${state === 'none' ? 'border-border bg-transparent' : ''}
                            ${!isLocked && state === 'none' ? 'hover:bg-muted/30' : ''}
                          `}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={isLocked ? undefined : () => togglePermission(domain, action)}
                            disabled={isLocked}
                          />
                          <span className={isOverride ? 'font-medium' : ''}>{actionLabel(action)}</span>
                          {!isLocked && isOverride && (
                            <span className="text-[10px] font-medium ms-auto">
                              {state === 'granted' ? t('hire.permissions.overrideStates.added') : t('hire.permissions.overrideStates.removed')}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          {!isLocked && (
            <span className="text-xs text-muted-foreground me-auto">
              {t('hire.permissions.overrideCount', { count: overrideCount })}
            </span>
          )}
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>
            {isLocked ? t('common.close') : t('common.cancel')}
          </Button>
          {!isLocked && (
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
              onClick={handleSave}
              disabled={updateOverrides.isPending}
            >
              {updateOverrides.isPending ? t('hire.account.saving') : t('hire.permissions.saveOverrides')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
